<template>
  <div class="custom-block-item-card">
    <block-item-card
      :title="$t('Rates.Section.FranchiseStrength.Title')"
      :title-info="$t('Rates.Section.FranchiseStrength.TitleInfo')"
      :tooltip="$t('Rates.Section.FranchiseStrength.Tooltip')"
      class="h-100"
    >
      <div class="pa-4">
        <v-lazy
          :options="{threshold: 0.5}"
          transition="fade-transition"
        >
          <div
            class="chart-container"
            style="height: 362px"
          >
            <e-chart
              ref="chart"
              :option="chartOption"
              :style="{height: '100%', width: '100%'}"
              @resize="handleChartResize"
            />
          </div>
        </v-lazy>
      </div>
    </block-item-card>
  </div>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import { BarChart } from 'echarts/charts'
import { use } from 'echarts/core'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { floor10 } from '@/lib/utils/helper'

use([
  BarChart
])

export default {
  name      : 'FranchiseStrength',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
    data: {
      type   : Array,
      default: () => []
    },
    highlightedBank: {
      type   : String,
      default: 'CTBC Bank'
    }
  },
  computed: {
    yAxisInterval () {
      const maxValue = Math.max(...this.data.map(item => item.value || 0), 0)
      if (maxValue >= 40) {
        return 10
      } else if (maxValue > 10) {
        return 5
      } else if (maxValue > 5) {
        return 2
      } else if (maxValue > 1.5) {
        return 0.5
      }
      return maxValue < 1.5 ? 0.2 : 0.5
    },
    yAxisMax () {
      const maxValue = Math.max(...this.data.map(item => item.value || 0), 0)
      let temp = 0
      if (maxValue >= 40) {
        if (maxValue % 10 >= 7) {
          temp = maxValue + 2
        } else {
          temp = maxValue + (10 - (maxValue % 10))
        }
      } else if (maxValue > 10) {
        if (maxValue % 5 >= 3) {
          temp = maxValue + 2
        } else {
          temp = maxValue + (5 - (maxValue % 5))
        }
      } else if (maxValue > 10) {
        if (maxValue % 5 >= 3) {
          temp = maxValue + 2
        } else {
          temp = maxValue + (5 - (maxValue % 5))
        }
      } else if (maxValue > 5) {
        if (maxValue % 2 >= 1) {
          temp = maxValue + 1
        } else {
          temp = maxValue + (2 - (maxValue % 2))
        }
      } else if (maxValue >= 1.5) {
        temp = maxValue + 0.25
      } else {
        temp = maxValue + 0.2
      }

      var final = Math.ceil(temp / this.yAxisInterval) * this.yAxisInterval
      return floor10(final, -1)
    },

    chartOption () {
      return {
        tooltip: {
          trigger    : 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' }
        },
        grid: {
          top         : '3%',
          left        : '5',
          right       : '0',
          bottom      : '3%',
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.data.map(item => item.bankName),
          axisLabel: {
            fontSize     : 10,
            fontFamily   : 'Inter, Aeonik, Roboto, sans-serif',
            fontWeight   : 400,
            fontStyle    : 'Regular',
            letterSpacing: '-0.2px',
            textAlign    : 'center',
            fontColor    : '#63747D',
            interval     : 0,
            lineHeight   : 16,
            formatter    : v => {
              const spaceIndices = []
              for (let i = 0; i < v.length; i++) {
                if (v[i] === ' ') spaceIndices.push(i)
              }
              if (spaceIndices.length > 1) {
                // Split at the second space
                const secondSpaceIndex = spaceIndices[1]
                return v.substring(0, secondSpaceIndex) + '\n' + v.substring(secondSpaceIndex + 1)
              }
              // Normal split for single space or no spaces
              return v.split(' ').join('\n')
            }
          },
          axisLine: {
            show     : false,
            lineStyle: {
              type: 'dashed'
            }
          },
          axisTick: { show: false }
        },
        yAxis: {
          type     : 'value',
          min      : 0,
          interval : this.yAxisInterval,
          max      : this.yAxisMax,
          axisLabel: {
            formatter: '{value}%'
          },
          splitLine: {
            show     : true,
            lineStyle: {
              type  : 'dashed',
              color : '#DDE2E6',
              width : 1.3,
              zIndex: 10
            }
          }
        },
        series: [{
          name: 'Franchise Strength',
          type: 'bar',
          data: this.data.map(item => ({
            value    : item.value,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color       : item.isBank ? this.themeColorRGBA('synthesis-ui-green-02') : this.themeColorRGBA('synthesis-ui-blue-04')
            }
          })),
          showBackground : true,
          backgroundStyle: {
            color: this.themeColorRGBA('synthesis-ui-grey-06')
          },
          barWidth: '80%',
          label   : {
            show         : true,
            position     : 'top',
            formatter    : '{c}%',
            fontSize     : 10,
            fontFamily   : 'Aeonik, Roboto, sans-serif',
            fontWeight   : 500,
            fontStyle    : 'Medium',
            letterSpacing: '-0.2px',
            textAlign    : 'center',
            fontColor    : '#63747D'
          }
        }]
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.handleChartResize()
    })
    window.addEventListener('resize', this.handleChartResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleChartResize)
  },
  methods: {
    handleChartResize () {
      this.$nextTick(() => {
        if (this.$refs.chart && this.$refs.chart.chart) {
          this.$refs.chart.chart.resize()
        }
      })
    }
  }
}
</script>

<style scoped>
.custom-block-item-card {
  min-height: 362px;
}

.chart-container {
  min-height: 300px;
}
</style>
